import React, { useState } from "react";
import styles from "./StatutTimeline.module.scss";
import tools from "../../../../../helpers/tools";
import FlotteStatutBadge from "../FlotteStatutBadge";
import useStore from "../../../../../context/useStore";
import { useAtom } from "jotai";
import { connectedUserAtom } from "../../../../../middlewares/AuthMiddleware";
import {
  filterStatutHistoriqueForVO,
  isVORole,
} from "../FlotteTable/FlotteUtils";

const VO_TRANSITIONS = {
  VO: { from: 51, to: 61 },
  VO_531: { from: 531, to: 631 },
};

const STATUTS_REQUIRING_PRICE_CHECK = [61, 631];

const StatutTimeline = ({
  statutHistorique,
  onAddStatut,
  vehicleStatut,
  item,
}) => {
  const [state] = useStore();
  const [connectedUser] = useAtom(connectedUserAtom);
  const [selectedStatut, setSelectedStatut] = useState("");
  const [showSelector, setShowSelector] = useState(false);
  const [priceCheckModal, setPriceCheckModal] = useState({
    isOpen: false,
    type: null,
    prixVente: null,
    pendingStatut: null,
  });

  // Fonction pour obtenir la couleur du statut (identique à FlotteStatutBadge)
  const getStatutColor = (statutCode) => {
    const statutsConstant = state?.constant?.items?.FLOTTE?.STATUTS_VEHICULE;
    if (!statutCode || !statutsConstant) return "#6c757d"; // couleur par défaut

    const statutRef = statutsConstant.find((s) => s.id === statutCode);
    return statutRef?.color || "#6c757d";
  };

  // Récupérer la liste des statuts disponibles
  const statutsConstant =
    state?.constant?.items?.FLOTTE?.STATUTS_VEHICULE || [];

  // Déterminer si l'utilisateur VO peut effectuer une transition
  const role = connectedUser?.role;
  const voTransition = VO_TRANSITIONS[role];
  const canVOTransition =
    isVORole(role) &&
    voTransition &&
    parseInt(vehicleStatut) === voTransition.from;

  // Libellé du statut cible pour le bouton VO
  const targetStatutLabel = canVOTransition
    ? statutsConstant.find((s) => s.id == voTransition.to)?.name
    : null;

  // Vérification du prix de vente et de la date VO avant passage au statut 61/631
  const checkPriceBeforeStatutChange = (statut) => {
    if (STATUTS_REQUIRING_PRICE_CHECK.includes(statut)) {
      const prixVente = item?.occasion?.prix_de_vente_definitif;
      const dateFacture = item?.occasion?.date_vo;
      const hasPrix =
        prixVente !== null &&
        prixVente !== undefined &&
        prixVente !== "" &&
        prixVente !== 0;
      const hasDateFacture =
        dateFacture !== null && dateFacture !== undefined && dateFacture !== "";

      if (!hasPrix || !hasDateFacture) {
        const missingFields = [];
        if (!hasPrix) missingFields.push("Prix de vente définitif TTC");
        if (!hasDateFacture) missingFields.push("Date VO");

        setPriceCheckModal({
          isOpen: true,
          type: "missing",
          prixVente: null,
          pendingStatut: statut,
          missingFields,
        });
        return false;
      }

      setPriceCheckModal({
        isOpen: true,
        type: "confirm",
        prixVente,
        dateFacture,
        pendingStatut: statut,
        missingFields: [],
      });
      return false;
    }
    return true;
  };

  const handlePriceCheckConfirm = () => {
    const statut = priceCheckModal.pendingStatut;
    setPriceCheckModal({
      isOpen: false,
      type: null,
      prixVente: null,
      pendingStatut: null,
    });
    if (onAddStatut && statut) {
      onAddStatut({ statut });
    }
  };

  const handlePriceCheckClose = () => {
    setPriceCheckModal({
      isOpen: false,
      type: null,
      prixVente: null,
      pendingStatut: null,
    });
  };

  // Fonction pour ajouter un nouveau statut
  const handleAddStatut = () => {
    if (selectedStatut && onAddStatut) {
      const statut = parseInt(selectedStatut);
      if (checkPriceBeforeStatutChange(statut)) {
        onAddStatut({ statut });
      }
      setSelectedStatut("");
      setShowSelector(false);
    }
  };

  // Fonction pour la transition VO directe
  const handleVOTransition = () => {
    if (canVOTransition && onAddStatut) {
      if (checkPriceBeforeStatutChange(voTransition.to)) {
        onAddStatut({ statut: voTransition.to });
      }
    }
  };

  // Fonction pour annuler l'ajout
  const handleCancelAdd = () => {
    setSelectedStatut("");
    setShowSelector(false);
  };

  // if (!statutHistorique || statutHistorique.length === 0) {
  //   return (
  //     <div className={styles.timelineEmpty}>
  //       <i className="fas fa-history text-muted"></i>
  //       <span className="text-muted ml-2">
  //         Aucun historique de statut disponible
  //       </span>
  //     </div>
  //   );
  // }

  // Filtrer l'historique selon le rôle VO
  const filteredHistorique = filterStatutHistoriqueForVO(
    statutHistorique,
    connectedUser?.role,
  );

  const sortedDateDescStatutHistorique = [...filteredHistorique].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );

  return (
    <>
      <div className={styles.timeline}>
        {/* Bouton de transition pour les utilisateurs VO */}
        {canVOTransition && (
          <div
            className={`${styles.timelineItem} ${styles.addStatutItem} ${
              filteredHistorique.length === 0 ? styles.last : ""
            }`}
          >
            <div className={styles.timelineDate}></div>
            <div className={styles.timelineConnector}>
              <div
                className={`${styles.timelineDot} ${styles.addStatutDot} border`}
              >
                <i className="fas fa-arrow-right"></i>
              </div>
              {filteredHistorique.length > 0 && (
                <div className={styles.timelineLine}></div>
              )}
            </div>
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <button
                  className="btn btn-primary mt-1 btn-sm mb-0"
                  onClick={handleVOTransition}
                >
                  <i className="fas fa-arrow-right mr-1"></i>
                  Passer en{" "}
                  {targetStatutLabel ? `${targetStatutLabel}` : voTransition.to}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sélecteur pour nouveau statut (admins uniquement) */}
        {onAddStatut && !isVORole(role) && (
          <div
            className={`${styles.timelineItem} ${styles.addStatutItem} ${
              filteredHistorique.length === 0 ? styles.last : ""
            }`}
          >
            {/* Timeline date - vide pour le sélecteur */}
            <div className={styles.timelineDate}></div>

            {/* Timeline connector */}
            <div className={styles.timelineConnector}>
              <div
                className={`${styles.timelineDot} ${styles.addStatutDot} border`}
              >
                <i className="fas fa-plus"></i>
              </div>
              {(showSelector || filteredHistorique.length > 0) && (
                <div className={styles.timelineLine}></div>
              )}
            </div>

            {/* Timeline content */}
            <div className={styles.timelineContent}>
              {!showSelector ? (
                <div className={styles.timelineHeader}>
                  <button
                    className="btn btn-default mt-1 btn-sm mb-0 text-lowercase"
                    onClick={() => setShowSelector(true)}
                  >
                    <i className="fas fa-plus mr-1"></i>
                    Ajouter un statut
                  </button>
                </div>
              ) : (
                <div className={styles.addStatutForm}>
                  <div className={styles.timelineHeader}>
                    <div className="">
                      <select
                        className="form-control rounded-pill"
                        value={selectedStatut}
                        onChange={(e) => setSelectedStatut(e.target.value)}
                      >
                        <option value="">Sélectionner un statut...</option>
                        {statutsConstant.map((statut) => (
                          <option key={statut.id} value={statut.id}>
                            {statut.name}
                          </option>
                        ))}
                      </select>
                      <div className="d-flex" style={{ gap: "0.5rem" }}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={handleAddStatut}
                          disabled={!selectedStatut}
                        >
                          <i className="fas fa-check mr-1"></i>
                          Valider
                        </button>
                        <button
                          className="btn btn-default btn-sm shadow-none border-0"
                          onClick={handleCancelAdd}
                        >
                          <i className="fas fa-times mr-1"></i>
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Historique des statuts existants */}
        {sortedDateDescStatutHistorique.map((item, index) => (
          <div
            key={index}
            className={`${styles.timelineItem} ${
              index === filteredHistorique.length - 1 ? styles.last : ""
            }`}
          >
            {/* Timeline date */}
            <div className={styles.timelineDate}>
              <small className="text-muted">
                {tools.formatDate(item.date)}
              </small>
            </div>

            {/* Timeline connector */}
            <div className={styles.timelineConnector}>
              <div
                className={`${styles.timelineDot} border`}
                style={{ color: getStatutColor(item.statut) }}
              >
                <i className="fas fa-circle"></i>
              </div>
              {index < filteredHistorique.length - 1 && (
                <div className={styles.timelineLine}></div>
              )}
            </div>

            {/* Timeline content */}
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <FlotteStatutBadge statut={item.statut} />
              </div>

              {item.utilisateur && (
                <div className={styles.timelineUser}>
                  <i className="fas fa-user text-muted mr-1"></i>
                  <span className="text-muted">{item.utilisateur}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modale de vérification du prix de vente pour statut 61 */}
      {priceCheckModal.isOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={handlePriceCheckClose}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {priceCheckModal.type === "missing" ? (
                    <>
                      <i className="fas fa-exclamation-triangle text-warning mr-2"></i>
                      Informations manquantes
                    </>
                  ) : (
                    <>
                      <i className="fas fa-info-circle text-primary mr-2"></i>
                      Confirmation du passage en statut{" "}
                      {priceCheckModal.pendingStatut}
                    </>
                  )}
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={handlePriceCheckClose}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                {priceCheckModal.type === "missing" ? (
                  <div>
                    <p className="mb-0">
                      <strong>
                        Impossible de passer au statut{" "}
                        {priceCheckModal.pendingStatut}.
                      </strong>
                    </p>
                    <p>
                      {priceCheckModal.missingFields?.length === 1 ? (
                        <>
                          Le champ{" "}
                          <strong>{priceCheckModal.missingFields[0]}</strong>{" "}
                          doit être renseigné dans les valeurs VO avant de
                          pouvoir effectuer cette transition.
                        </>
                      ) : (
                        <>
                          Les champs suivants doivent être renseignés dans les
                          valeurs VO avant de pouvoir effectuer cette transition
                          :
                          <ul className="mt-2 mb-0">
                            {priceCheckModal.missingFields?.map((field) => (
                              <li key={field}>
                                <strong>{field}</strong>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </p>
                    <p className="text-muted mb-0">
                      Veuillez d'abord renseigner{" "}
                      {priceCheckModal.missingFields?.length === 1
                        ? "ce champ"
                        : "ces champs"}
                      , puis réessayer.
                    </p>
                  </div>
                ) : (
                  <div>
                    <p>Les valeurs actuelles sont :</p>
                    <div className="d-flex justify-content-around mb-3">
                      <div className="text-center">
                        <small className="text-muted d-block mb-1">
                          Prix de vente définitif TTC
                        </small>
                        <span
                          className="badge badge-primary"
                          style={{ fontSize: "1.1em", padding: "0.5em 1em" }}
                        >
                          {Number(priceCheckModal.prixVente).toFixed(2)} €
                        </span>
                      </div>
                      <div className="text-center">
                        <small className="text-muted d-block mb-1">
                          Date VO
                        </small>
                        <span
                          className="badge badge-primary"
                          style={{ fontSize: "1.1em", padding: "0.5em 1em" }}
                        >
                          {tools.formatDate(priceCheckModal.dateFacture)}
                        </span>
                      </div>
                    </div>
                    <div className="alert alert-warning mb-0">
                      <i className="fas fa-lock mr-2"></i>
                      <strong>Attention :</strong> après le passage au statut{" "}
                      {priceCheckModal.pendingStatut}, le{" "}
                      <strong>Prix de vente définitif TTC</strong> et la{" "}
                      <strong>Date VO</strong> ne seront{" "}
                      <strong>plus modifiables</strong>.
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {priceCheckModal.type === "missing" ? (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handlePriceCheckClose}
                  >
                    Compris
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={handlePriceCheckClose}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handlePriceCheckConfirm}
                    >
                      <i className="fas fa-check mr-1"></i>
                      Confirmer le passage
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StatutTimeline;
